import React from 'react';
import "../css/preview-content.css";
import {
  SinglePreviewCmp,
  SingleDescriptionCmp,
  TitleToggleCmp,
  SingleImageCmp,
  SingleImageButtonCmp,
  TitleSectionToggleCmp
} from './preview-component';

type DiffItem = {
  published: unknown;
  draft: unknown;
};

type NormalizedAnyPage = Record<string, any> & {
  page: string;
};

type Props = {
  pageKey: string; //NOSONAR
  diffData: Record<string, DiffItem>;
  draftData: NormalizedAnyPage | null;
  publishedData: NormalizedAnyPage | null;
  pageStatus?: string; //NOSONAR
  isReady: boolean;
  mode?: string;
};

const PREVIEW_KEYS = new Set(["title", "label", "buttonLabel", "buttonTitle", "buttonText", "first", "second", "headline"]);
const DESCRIPTION_KEYS = new Set(["description", "paragraph", "subDescription"]);
const IMAGE_KEYS = new Set(["image", "img", "imgLogo", "imageSrc", "imgIco", "src",]);
const ICON_KEYS = new Set(["icon", "iconSrc", "Icon"]);

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const getFieldTitle = (key: string, path = "") => {
  if (
    key === "buttonLabel" ||
    key === "buttonTitle" ||
    key === "buttonText" ||
    path.includes("primaryButton.label")
  ) {
    return "Button";
  }

  if (key === "description" || key === "paragraph" || key === "subDescription") {
    return "Sub title / Description";
  }

  if (IMAGE_KEYS.has(key)) {
    return "Image";
  }

  if (ICON_KEYS.has(key)) {
    return "Icon";
  }

  return "Title";
};

const getVisibleStatus = (obj: unknown): boolean | undefined => {
  if (!isPlainObject(obj)) return undefined;
  if (typeof obj.visible === "boolean") return obj.visible;
  return undefined;
};

const buildSectionsToRender = (
  draftData: NormalizedAnyPage | null,
  publishedData: NormalizedAnyPage | null
): Record<string, DiffItem> => {
  if (!draftData) return {};

  const result: Record<string, DiffItem> = {};
  const keys = Object.keys(draftData).filter(
    (key) => key !== "page" && key !== "meta"
  );

  for (const key of keys) {
    result[key] = {
      published: publishedData?.[key],
      draft: draftData[key],
    };
  }

  return result;
};

const buildSingleSectionsToRender = (
  data: NormalizedAnyPage | null
): Record<string, DiffItem> => {
  if (!data) return {};

  const result: Record<string, DiffItem> = {};
  const keys = Object.keys(data).filter(
    (key) => key !== "page" && key !== "meta"
  );

  for (const key of keys) {
    result[key] = {
      published: data[key],
      draft: data[key],
    };
  }

  return result;
};

const getImageAlt = (obj: unknown): string => {
  if (!isPlainObject(obj)) return "";
  return String(obj.imgAlt ?? obj.iconAlt ?? ""); //NOSONAR
};

const getImageTitle = (obj: unknown): string => {
  if (!isPlainObject(obj)) return "Image";
  return String(obj.title ?? obj.label ?? "Image"); //NOSONAR
};

const getImageSize = (_src: string): string => {
  return "Image file";
};

const hasIconObjectShape = (value: unknown): value is Record<string, unknown> => {
  if (!isPlainObject(value)) return false;

  return (
    "icon" in value ||
    "iconSrc" in value ||
    "Icon" in value
  );
};

const getIconSrc = (obj: Record<string, unknown>): string => {
  return String(
    obj.icon ?? //NOSONAR
    obj.iconSrc ??
    obj.Icon ??
    ""
  );
};

const getIconLabel = (obj: unknown): string => {
  if (!isPlainObject(obj)) return "";
  return String(obj.label ?? ""); //NOSONAR
};

const getIconAlt = (obj: unknown): string => {
  if (!isPlainObject(obj)) return "";
  return String(obj.iconAlt ?? obj.imgAlt ?? ""); //NOSONAR
};

const renderMatchedFields = (
  published: unknown,
  draft: unknown,
  path = ""
): React.ReactNode[] => { //NOSONAR
  const elements: React.ReactNode[] = [];

  if (draft === undefined || draft === null) {
    return elements;
  }

  if (typeof draft === "string" && !isNonEmptyString(draft)) {
    return elements;
  }

  if (Array.isArray(draft)) {
    const publishedArray = Array.isArray(published) ? published : [];

    for (let i = 0; i < draft.length; i++) {
      const itemFields = renderMatchedFields(
        publishedArray[i],
        draft[i],
        `${path}[${i}]`
      );

      if (!itemFields.length) continue;

      elements.push(
        <div className="preview-list-item" key={`${path}[${i}]`}>
          <div className="preview-list-item-body">
            {itemFields}
          </div>
        </div>
      );
    }

    return elements;
  }

  if (isPlainObject(draft)) {
    const publishedObject = isPlainObject(published) ? published : {};

    const parentVisible =
      getVisibleStatus(draft) ?? getVisibleStatus(publishedObject);

    const keys = Object.keys(draft).filter(
      (key) => key !== "page" && key !== "meta"
    );

    for (const key of keys) {
      const publishedValue = publishedObject[key];
      const draftValue = draft[key];
      const nextPath = path ? `${path}.${key}` : key;

      if (draftValue === undefined || draftValue === null) {
        continue;
      }

      if (typeof draftValue === "string" && !isNonEmptyString(draftValue)) {
        continue;
      }

      if (
        PREVIEW_KEYS.has(key) &&
        typeof draftValue === "string"
      ) {
        elements.push(
          <React.Fragment key={nextPath}>
            <TitleToggleCmp
              title={getFieldTitle(key, nextPath)}
              toggleEnable={typeof parentVisible === "boolean"}
              toggleStatus={parentVisible}
            />
            <SinglePreviewCmp
              title={typeof publishedValue === "string" ? publishedValue : ""}
              valueInput={draftValue}
            />
          </React.Fragment>
        );
        continue;
      }

      if (
        DESCRIPTION_KEYS.has(key) &&
        typeof draftValue === "string"
      ) {
        elements.push(
          <React.Fragment key={nextPath}>
            <TitleToggleCmp
              title={getFieldTitle(key, nextPath)}
              toggleEnable={typeof parentVisible === "boolean"}
              toggleStatus={parentVisible}
            />
            <SingleDescriptionCmp
              description={typeof publishedValue === "string" ? publishedValue : ""}
              valueTextarea={draftValue}
            />
          </React.Fragment>
        );
        continue;
      }

      if (
        IMAGE_KEYS.has(key) &&
        isNonEmptyString(draftValue)
      ) {
        elements.push(
          <React.Fragment key={nextPath}>
            <TitleToggleCmp
              title="Image"
              toggleEnable={typeof parentVisible === "boolean"}
              toggleStatus={parentVisible}
            />
            <SingleImageCmp
              imgSrc={draftValue}
              imgAlt={getImageAlt(draft)}
              imgTitle={getImageTitle(draft)}
              imgSize={getImageSize(draftValue)}
            />
          </React.Fragment>
        );
        continue;
      }

      if (
        ICON_KEYS.has(key) &&
        isNonEmptyString(draftValue)
      ) {
        elements.push(
          <React.Fragment key={nextPath}>
            <TitleToggleCmp
              title="Icon"
              toggleEnable={typeof parentVisible === "boolean"}
              toggleStatus={parentVisible}
            />
            <SingleImageButtonCmp
              imgSrc={draftValue}
              imgAlt={getIconAlt(draft)}
              imgTitle={getIconAlt(draft)}
              valueInput={getIconAlt(draft)}
            />
          </React.Fragment>
        );
        continue;
      }

      elements.push(
        ...renderMatchedFields(
          publishedValue,
          draftValue,
          nextPath
        )
      );
    }
  }

  return elements;
};

const isNonEmptyString = (value: unknown): value is string => {
  return typeof value === "string" && value.trim() !== "";
};

const getSectionVisible = (value: unknown): boolean | undefined => {
  if (!isPlainObject(value)) return undefined;

  // only read visible from THIS level
  if (Object.prototype.hasOwnProperty.call(value, "visible")) {
    if (typeof value.visible === "boolean") {
      return value.visible;
    }
  }

  return undefined;
};

const PreviewContent: React.FC<Props> = ({
  diffData,
  draftData,
  publishedData,
  isReady,
  mode,
}) => {
  if (!isReady) {
    return <div>Loading preview...</div>;
  }

  const source =
    mode === "published"
      ? buildSingleSectionsToRender(publishedData)
      : buildSectionsToRender(draftData, publishedData);

  const sectionsToRender = Object.entries(source);

  if (!sectionsToRender.length) {
    return <div>No data to preview</div>;
  }

  return (
    <main className="scl--pvc-box-container-style">
      {sectionsToRender.map(([sectionKey, value]) => {
        const matchedFields = renderMatchedFields(value.published, value.draft);

        if (!matchedFields.length) return null;

        const sectionVisible =
          getSectionVisible(value.draft) ??
          getSectionVisible(value.published);

        const toggleEnable = sectionVisible !== undefined;
        const toggleStatus = sectionVisible === true;

        return (
          <div className="diff-section-box" key={sectionKey}>
            <TitleSectionToggleCmp
              title={sectionKey}
              toggleEnable={toggleEnable}
              toggleStatus={toggleStatus}
            />
            {matchedFields}
          </div>
        );
      })}
    </main>
  );
};

export default PreviewContent;